/**
 * Browser-side client for the webapp's `/api/*` routes (which proxy
 * to builder-api with the session's access token attached server-
 * side). The browser never sees the access token directly.
 */

export interface SiteSummary {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly role: "owner" | "editor" | "viewer";
  readonly createdAt: string;
}

export interface CreateSiteResult {
  readonly id: string;
  readonly slug: string;
  readonly plan: "free" | "pro";
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`api error ${status}`);
    this.name = "ApiError";
  }
}

async function send<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const r = await fetch(input, {
    credentials: "same-origin",
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  const text = await r.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }
  if (!r.ok) throw new ApiError(r.status, parsed);
  return parsed as T;
}

export interface SiteDetail {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly role: "owner" | "editor" | "viewer";
  readonly createdAt: string;
}

// /api/me — session + plan + usage (T3.18). `plan` + `usage` are
// optional because builder-api may be unreachable or the account row
// hasn't been provisioned yet; the SPA must handle both shapes.
export interface MeResponse {
  readonly sub: string;
  readonly email: string;
  readonly plan?: {
    readonly tier: string;
    readonly storageLimitBytes: number;
    readonly pushSizeLimitBytes: number;
    readonly assetSizeLimitBytes: number;
    readonly maxSitesPerUser: number;
    readonly brandingRequired: boolean;
  };
  readonly usage?: {
    readonly storageBytes: number;
    readonly sitesCount: number;
  };
}

// /api/sites/:id/slug — vanity slug claim (T3.19).
export interface ClaimSlugInput {
  readonly slug: string;
}
export interface ClaimSlugResult {
  readonly id: string;
  readonly slug: string;
  readonly previousSlug: string;
}

// /api/sites/:id/assets — list assets for media library (T3.20).
export interface AssetSummary {
  readonly hash: string;
  readonly filename: string;
  readonly contentType: string;
  readonly sizeBytes: number;
  readonly uploadedAt: string;
}

export const api = {
  me: () => send<MeResponse>("/api/me"),
  listSites: () => send<ReadonlyArray<SiteSummary>>("/api/sites"),
  createSite: (input: { name: string; templateId: string }) =>
    send<CreateSiteResult>("/api/sites", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  getSite: (id: string) =>
    send<SiteDetail>(`/api/sites/${encodeURIComponent(id)}`),
  claimSlug: (id: string, input: ClaimSlugInput) =>
    send<ClaimSlugResult>(`/api/sites/${encodeURIComponent(id)}/slug`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  listAssets: (id: string) =>
    send<ReadonlyArray<AssetSummary>>(
      `/api/sites/${encodeURIComponent(id)}/assets`,
    ),
};
